import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./HeaderBar.module.scss";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Button } from "@heroui/react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import LoginModal from "../Modals/loginModal";
import GlassPanel from "../lightbox/GlassPanel";
import { Bars, Xmark } from "@gravity-ui/icons";
/* UI-DESIGN: 2026-10-25 (Taco: no logo on the preview) ?no-inline ships the
   logo as a file. Vite inlined it as a data: URI, which the site's security
   policy (default-src https:) blocks, so the header showed nothing. */
import logoB from "../../assets/brand/logo-b-red-pencil.svg?no-inline";

/* UI-DESIGN: LB-50 logo B (the red-pencil ring round "who") is final, so
   the ?logo toggle is gone. C's wax-seal SVGs stay in src/assets/brand/ as
   files only. Outlined SVG from docs/lightbox/lb48/make-logos.py, so no web
   font and crisp at any size. */

const NAV_LINKS: { to: string; label: string; end?: boolean }[] = [
  { to: "/mysteries", label: "Mysteries", end: true },
  /* UI-DESIGN: Open Cases = the mysteries you're registered to */
  { to: "/about", label: "How it works" },
  { to: "/open-cases", label: "Open Cases", end: true },
];

/* UI-DESIGN: LB-73 the phone menu's links: the nav plus Host a party,
   which the desktop shows as its own button */
const MENU_LINKS = [...NAV_LINKS, { to: "/create-game", label: "Host a party", end: true }];

const HeaderBar: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useSelector((state: RootState) => state.login.user);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTop, setMenuTop] = useState(0);
  const [menuHost, setMenuHost] = useState<Element | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  /* UI-DESIGN: LB-73 any route change folds the menu away */
  useEffect(() => setMenuOpen(false), [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setMenuOpen(false);
      trigger.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const toggleMenu = () => {
    /* the menu hangs from the bar's bottom edge, wherever the bar sits */
    setMenuTop(trigger.current?.closest("header")?.getBoundingClientRect().bottom ?? 0);
    setMenuHost(document.querySelector("[data-lb-surface]") ?? document.body);
    setMenuOpen((open) => !open);
  };

  /* UI-DESIGN: LB-73 (Taco's B pick, 2026-10-18): the menu drops out of
     the full-width bar as the same smoked glass, typeset links on
     hairlines, instead of HeroUI's floating popover (it read clunky). A
     disclosure of links, not a menu widget, so it is plain buttons and
     NavLinks. It is portalled beside the bar, not inside it: glass nested
     in the bar's glass would blur the bar instead of the page. On the
     lightbox it goes into the surface so it reads the surface's tokens. */
  const menu = (
    <div
      className={clsx(styles.menuScrim, menuOpen && styles.menuScrimOpen)}
      style={{ top: menuTop }}
      onClick={() => setMenuOpen(false)}
    >
      <nav id="phone-menu" className={styles.menuDrop} aria-label="Main" onClick={(e) => e.stopPropagation()}>
        <ul className={styles.menuLinks}>
          {MENU_LINKS.map(({ to, label, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) => clsx(styles.menuLink, isActive && styles.menuLinkActive)}
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );

  return (
    /* UI-DESIGN: LB-19 the nav is the same GlassPanel as every card on the
       Home; the skin comes from the header's --glass-* tints */
    <GlassPanel as="header" variant="panel" className={styles.header}>
      {/* UI-DESIGN: LB-50 logo B, final */}
      <NavLink to="/" className={styles.logoLink} aria-label="Whodunit home">
        <img className={clsx(styles.logoImg, styles.logoB)} src={logoB} alt="" draggable={false} />
      </NavLink>
      <div className={styles.spacer} aria-hidden />
      <div className={styles.desktopNav}>
        <nav className={styles.nav} aria-label="Main">
          <ul className={styles.navList}>
            {NAV_LINKS.map(({ to, label, end }) => (
              <li key={to} className={styles.navItem}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    clsx(styles.navLink, isActive && styles.navLinkActive)
                  }
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className={styles.actions}>
            {/* UI-DESIGN: cream pill treatment for the primary header CTA (per home mockup) */}
            <Button
              type="button"
              variant="primary"
              className={styles.hostButton}
              onPress={() => navigate("/create-game")}
            >
              Host Party
            </Button>
            <div className={styles.loginButton}>
              <LoginModal />
            </div>
          </div>
        </nav>
      </div>
      <div className={styles.mobileMenu}>
        {/* UI-DESIGN: LB-60b (Avi) players arriving to play find Log in in
            the bar itself on phones, not inside the menu */}
        <div className={clsx(styles.mobileAccount, !user && styles.loginButton)}>
          <LoginModal dialog={false} />
        </div>
        <button
          ref={trigger}
          type="button"
          className={styles.menuTrigger}
          aria-expanded={menuOpen}
          aria-controls="phone-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={toggleMenu}
        >
          {menuOpen ? <Xmark width={22} height={22} aria-hidden /> : <Bars width={22} height={22} aria-hidden />}
        </button>
        {menuHost ? createPortal(menu, menuHost) : null}
      </div>
    </GlassPanel>
  );
};

export default HeaderBar;
